import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import RichTextEditor from "@/components/ui/rich-text";
import { STATUS_LABELS, PRIORITY_LABELS } from "@/lib/taskHelpers";

export default function TaskFormDialog({
  open,
  onOpenChange,
  labels,
  members,
  onCreated,
  defaults = {},
}) {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [status, setStatus] = useState("todo");
  const [priority, setPriority] = useState("medium");
  const [due, setDue] = useState("");
  // Defaults per spec:
  //   • assignee → the user creating the task (not "Unassigned")
  //   • visibility → "private" (not "workspace")
  // User can still change both manually before submit.
  const [assignee, setAssignee] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [labelIds, setLabelIds] = useState([]);
  // Subtasks live on the form so the user can scaffold them out at
  // create time. Submitted via the existing POST /tasks/{id}/subtasks
  // endpoint after the parent task is created — no duplicate subtask
  // system, just the one already wired up on the backend.
  const [subtasks, setSubtasks] = useState([]); // [{ tmp_id, title }]
  const [subDraft, setSubDraft] = useState("");
  // Workspace-scoped custom statuses (loaded once per open). Falls back
  // to the static STATUS_LABELS if the call fails or hasn't returned
  // yet — never blocks the user from creating a task.
  const [statuses, setStatuses] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(defaults.title || "");
    setDesc(defaults.description || "");
    setStatus(defaults.status || "todo");
    setPriority(defaults.priority || "medium");
    setDue(defaults.due_date || "");
    // Per-spec defaults: assignee = current user, visibility = private.
    // Defaults block still wins if the caller explicitly passes one
    // (e.g. follow-up generators that hand-build a workspace task).
    setAssignee(
      defaults.assignee_id !== undefined
        ? (defaults.assignee_id || "")
        : (user?.user_id || "")
    );
    setVisibility(defaults.visibility || "private");
    setLabelIds(defaults.label_ids || []);
    setSubtasks([]);
    setSubDraft("");
    // Pull the workspace's status list (built-in + custom). Backend
    // auto-seeds defaults on first hit, so this is safe to call once.
    if (currentWorkspace?.workspace_id) {
      api
        .get("/statuses", { params: { workspace_id: currentWorkspace.workspace_id } })
        .then((r) => setStatuses(r.data || []))
        .catch(() => setStatuses(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addSubtask = () => {
    const t = subDraft.trim();
    if (!t) return;
    setSubtasks((arr) => [...arr, { tmp_id: `s_${Date.now()}_${arr.length}`, title: t }]);
    setSubDraft("");
  };
  const removeSubtask = (id) => setSubtasks((arr) => arr.filter((s) => s.tmp_id !== id));

  const submit = async () => {
    if (!title.trim()) { toast.error("Task needs a title"); return; }
    // TipTap emits an empty paragraph for empty content — normalize so
    // we don't persist `<p></p>` strings on every brand-new task.
    const trimmedDesc = (desc || "").trim();
    const cleanDesc = trimmedDesc === "<p></p>" ? "" : trimmedDesc;
    setBusy(true);
    try {
      const { data: task } = await api.post("/tasks", {
        workspace_id: currentWorkspace.workspace_id,
        title: title.trim(),
        description: cleanDesc,
        status,
        priority,
        due_date: due || null,
        assignee_id: assignee || null,
        visibility,
        label_ids: labelIds,
        related_meeting_id: defaults.related_meeting_id || null,
        related_contact_id: defaults.related_contact_id || null,
      });
      // Create subtasks after the parent so they get a real parent_task_id.
      for (const s of subtasks) {
        try {
          await api.post(`/tasks/${task.task_id}/subtasks`, { title: s.title });
        } catch {
          // best-effort; if one fails, surface a soft warning and keep going
          toast.error(`Subtask "${s.title.slice(0, 30)}…" failed to save`);
        }
      }
      toast.success("Task created");
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create task");
    } finally {
      setBusy(false);
    }
  };

  const toggleLabel = (id) => {
    setLabelIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Status options: prefer workspace-loaded list. Fall back to the
  // static STATUS_LABELS map so an offline form still works.
  const statusOptions = statuses && statuses.length
    ? statuses.map((s) => ({ key: s.key, name: s.name }))
    : Object.entries(STATUS_LABELS).map(([k, v]) => ({ key: k, name: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 mt-2">
          <Field label="Title">
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="bloq-input" placeholder="What needs to happen?" data-testid="taskform-title" autoFocus />
          </Field>
          <Field label="Description (optional)">
            <RichTextEditor
              value={desc}
              onChange={setDesc}
              placeholder="Context, links, anything that helps…"
              testId="taskform-description"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="taskform-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statusOptions.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="taskform-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Due date">
              <input type="date" value={due ? due.slice(0,10) : ""} onChange={(e) => setDue(e.target.value ? new Date(e.target.value).toISOString() : "")} className="bloq-input" data-testid="taskform-due" />
            </Field>
            <Field label="Assignee">
              <Select value={assignee || "none"} onValueChange={(v) => setAssignee(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="taskform-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}{user?.user_id === m.user_id ? " (you)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Visibility">
            <div className="flex gap-2">
              {["private", "workspace"].map((v) => (
                <button
                  key={v}
                  onClick={() => setVisibility(v)}
                  className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                  style={{ background: visibility === v ? "#005e7a" : "#f5efe6", color: visibility === v ? "#fff" : "#1a1a1f" }}
                  data-testid={`taskform-visibility-${v}`}
                >
                  {v === "workspace" ? "Workspace" : "Private (just me)"}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Labels">
            <div className="flex flex-wrap gap-1.5">
              {labels.map((l) => {
                const on = labelIds.includes(l.label_id);
                return (
                  <button
                    key={l.label_id}
                    onClick={() => toggleLabel(l.label_id)}
                    className="px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors"
                    style={{
                      background: on ? l.color : `${l.color}1a`,
                      color: on ? "#fff" : l.color,
                      borderColor: l.color,
                    }}
                    data-testid={`taskform-label-${l.label_id}`}
                  >
                    {l.name}
                  </button>
                );
              })}
            </div>
          </Field>
          {/* Subtasks. Mirrors the existing in-drawer subtask UI so the
             user can scaffold them at create time. Each row is just a
             title; subtasks are stored as full tasks with parent_task_id
             so they get the same status / priority / assignee flexibility
             once created. */}
          <Field label="Subtasks (optional)">
            <div className="flex flex-col gap-1.5">
              {subtasks.map((s, i) => (
                <div key={s.tmp_id} className="flex items-center gap-2 bg-[#f5efe6] rounded-md px-2 py-1.5" data-testid={`taskform-subtask-${i}`}>
                  <span className="text-sm text-[#1a1a1f] flex-1 truncate">{s.title}</span>
                  <button
                    onClick={() => removeSubtask(s.tmp_id)}
                    className="text-[#82828c] hover:text-[#dc2626] p-0.5"
                    aria-label="Remove subtask"
                    data-testid={`taskform-subtask-remove-${i}`}
                  ><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <input
                  value={subDraft}
                  onChange={(e) => setSubDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSubtask(); } }}
                  className="bloq-input flex-1"
                  placeholder="Add a subtask and press Enter"
                  data-testid="taskform-subtask-input"
                />
                <button
                  type="button"
                  onClick={addSubtask}
                  disabled={!subDraft.trim()}
                  className="px-3 py-1.5 bg-[#005e7a] text-white rounded-md inline-flex items-center gap-1 text-xs disabled:opacity-50"
                  data-testid="taskform-subtask-add"
                ><Plus className="w-3 h-3" /> Add</button>
              </div>
            </div>
          </Field>
          <button onClick={submit} disabled={busy} className="bloq-btn-primary mt-2" data-testid="taskform-submit">
            {busy ? "Creating…" : "Create task"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
